import PropTypes from 'prop-types';
import React from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@opendate-app/setlist';
import { dragEndAction, filterDroppables, groupId, itemId } from '../layoutReducer';

const SortableRow = ({ id, className, testId, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  return (
    <div ref={setNodeRef} className={className} data-testid={testId} style={{ transform: CSS.Transform.toString(transform), transition }}>
      <span className="ntype-handle" aria-label="Drag to reorder" {...attributes} {...listeners}>⠿</span>
      {children}
    </div>
  );
};

SortableRow.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  testId: PropTypes.string,
  children: PropTypes.node,
};

const LayoutEditor = ({ layout, registry, labels, dispatch }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const collisionDetection = (args) => closestCenter({
    ...args,
    droppableContainers: filterDroppables(layout, args.active.id, args.droppableContainers),
  });

  const handleDragEnd = ({ active, over }) => {
    const action = dragEndAction(layout, active.id, over?.id);
    if (action) dispatch(action);
  };

  return (
    <div className="ntype-layout">
      <div className="ntype-group ntype-group-locked">
        <div className="ntype-group-head">🔒 {registry.locked.label} <span className="ntype-muted">always first</span></div>
        {registry.locked.items.map((item) => <div key={item.key} className="ntype-item">{item.label}</div>)}
      </div>

      <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragEnd={handleDragEnd}>
        <SortableContext items={layout.map((group) => groupId(group.key))} strategy={verticalListSortingStrategy}>
          {layout.map((group) => (
            <SortableRow key={group.key} id={groupId(group.key)} testId={`group-${group.key}`} className={`ntype-group${group.hidden ? ' ntype-hidden' : ''}`}>
              <div className="ntype-group-head">
                <input
                  className="ntype-label-input"
                  value={group.label || ''}
                  placeholder={labels.groups[group.key]}
                  aria-label={`Rename ${labels.groups[group.key]}`}
                  onChange={(event) => dispatch({ type: 'renameGroup', groupKey: group.key, label: event.target.value })}
                />
                <Button size="xs" variant="tertiary" onClick={() => dispatch({ type: 'toggleGroupHidden', groupKey: group.key })}>
                  {group.hidden ? 'Show' : 'Hide'}
                </Button>
              </div>
              <SortableContext items={group.items.map((item) => itemId(item.key))} strategy={verticalListSortingStrategy}>
                {group.items.map((item) => (
                  <SortableRow key={item.key} id={itemId(item.key)} className={`ntype-item${item.hidden ? ' ntype-hidden' : ''}`}>
                    <span className="ntype-item-default">{labels.items[item.key]}</span>
                    <input
                      className="ntype-label-input"
                      value={item.label || ''}
                      placeholder="Rename…"
                      aria-label={`Rename ${labels.items[item.key]}`}
                      onChange={(event) => dispatch({ type: 'renameItem', itemKey: item.key, label: event.target.value })}
                    />
                    <select
                      className="ntype-move"
                      data-testid={`move-item-${item.key}`}
                      aria-label={`Move ${labels.items[item.key]} to group`}
                      value={group.key}
                      onChange={(event) => dispatch({ type: 'moveItem', itemKey: item.key, toGroupKey: event.target.value, toIndex: Number.MAX_SAFE_INTEGER })}
                    >
                      {layout.map((option) => <option key={option.key} value={option.key}>{option.label || labels.groups[option.key]}</option>)}
                    </select>
                    <button
                      type="button"
                      className="ntype-eye"
                      data-testid={`toggle-item-${item.key}`}
                      onClick={() => dispatch({ type: 'toggleItemHidden', itemKey: item.key })}
                    >
                      {item.hidden ? 'Show' : 'Hide'}
                    </button>
                  </SortableRow>
                ))}
              </SortableContext>
            </SortableRow>
          ))}
        </SortableContext>
      </DndContext>
      <p className="ntype-muted">Drag ⠿ to reorder groups and items, or use “Move to” for items. A blank name keeps the default.</p>
    </div>
  );
};

LayoutEditor.propTypes = {
  layout: PropTypes.arrayOf(PropTypes.object).isRequired,
  registry: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default LayoutEditor;
